/**
 * @lines98/client/components/board-preview-card — ПРИМИТИВ «карточка-превью режима с
 * ЖИВОЙ мини-доской». ОДИН источник вида/поведения оболочки карточки для ОБЕИХ
 * поверхностей (модель «ствол → ветки», CLAUDE.md / code-structure §1/§2).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-25, тикет card-preview-primitive):
 *   Карточка-режима-с-живой-доской была реализована ДВАЖДЫ — рабочая в лобби «Играть»
 *   (`components/mode-card`) и дублирующий билдер на стартовой-лендинге
 *   (`screens/start-screen` + стили `.start-cards .mode-card--landing` в `start.css`).
 *   Дубль на стартовой нёс три бага: (а) серый ореол-«рамка» вокруг доски (плоская
 *   карточка без приподнятости → собственная тень `.board-frame` выпирала на белой
 *   плашке), (б) лаг прокрутки мимо живых досок (paint всех клеток на каждом кадре),
 *   (в) имена режимов рассинхронились по реализациям. Owner: «вынести рабочую карточку
 *   в ПРИМИТИВ и переиспользовать на стартовой» → все 3 бага уходят, т.к. стартовая
 *   наследует рабочую оболочку лобби (приподнятость + paint-изоляция + локализация имени).
 *
 * SCOPE (что владеет — ОДИН источник правды на оболочку):
 *   · ХРОМ карточки: рамка/радиус/padding/фон/ПРИПОДНЯТОСТЬ box-shadow /
 *     PAINT-ИЗОЛЯЦИЯ (`contain: layout paint` + кэш-композит-слой живой доски НА ПК —
 *     `translateZ(0)`, эталон серых locked-досок; БЕЗ content-visibility/RO — они вызывали
 *     лаг+прыжок скролла, см. board-preview-card.css шапку + замер). Всё — в
 *     `board-preview-card.css` (co-located).
 *   · ЖИВАЯ мини-доска: `mountBoardView` (static, non-interactive) + общий
 *     `renderModeThumbPreview` (predict→госты+ниша / classic→ниша / blind→голо) +
 *     `attachLiveAppearance` (превью в оформлении игрока, реактивно). Размер/вписывание —
 *     общий `components/mode-thumb.css` (cqw от `.mode-card-thumb`).
 *   · ИМЯ режима: must-visible text-узел `.mode-card-name`, ЛОКАЛИЗОВАННОЕ
 *     (`modeLabel(entry, lang)` — единый источник, закрывает leak имени на ОБОИХ экранах).
 *   · locked: приглушение + бейдж «Скоро» оверлеем на углу доски + grayscale.
 *
 * NOT IN SCOPE (живёт в «ветке»-экране, не в примитиве):
 *   · «?»-инфо-попап пояснения (лобби) — `mountInfoPopover` поверх `nameRowEl`;
 *   · ОПИСАНИЕ под именем (лендинг) — `<p class="mode-card-desc">` в `bodyEl`;
 *   · клик-в-игру / клик-в-превью (каждый экран свой путь навигации);
 *   · РАСКЛАДКА карточки (лобби — всегда вертикаль; лендинг — media-object на узком ряду,
 *     витрина на широком). Раскладку задаёт CSS-скоуп экрана (`mode-select.css` / `start.css`)
 *     на ОБЩИХ классах `.mode-card-thumb` / `.mode-card-name` / `.mode-card-desc`. Примитив
 *     даёт нейтральную структуру (thumb → name-row → body), а не фиксированную раскладку.
 *
 * Карта — `<div>` (НЕ `<button>`): inner «?»-кнопка (лобби) = invalid nested button.
 * `role`/`tabindex`/click-wiring вешает ЭКРАН (передаёт `interactive` или сам навешивает
 * на `element`) — примитив a11y-нейтрален к выбору цели, но НЕ к доске (она `aria-hidden`).
 *
 * Cross-references: components/mode-card (лобби-ветка), screens/start-screen (лендинг-ветка),
 *   lib/board-view, lib/mode-thumb-preview, lib/board-appearance, lib/mode-registry (modeLabel),
 *   components/mode-thumb.css; responsive-ui §0.A/§5; ui-performance (paint-изоляция скролла);
 *   code-structure §1 (ствол→ветки) / §2 (примитив с a11y+токенами внутри).
 */

import './board-preview-card.css';
import './mode-thumb.css';

import { attachLiveAppearance, type LiveAppearanceHandle } from '../lib/board-appearance.js';
import { mountBoardView, type BoardViewHandle } from '../lib/board-view.js';
import { t } from '../lib/i18n/index.js';
import { modeLabel, type ModeEntry } from '../lib/mode-registry.js';
import { renderModeThumbPreview } from '../lib/mode-thumb-preview.js';
import type { ContentLang } from '@lines98/shared/config/seo';

export interface BoardPreviewCardOptions {
  /** Registry entry — единый источник имени (modeLabel), уровня подсказки, locked/badge. */
  readonly entry: ModeEntry;
  /**
   * Язык для имени режима — ЛОКАЛИЗАЦИЯ через `modeLabel(entry, lang)` (единый источник).
   * Лобби передаёт `getLocale()`, лендинг — язык страницы; оба резолвят валидный
   * `ContentLang` ДО вызова (закрывает leak имени на обоих экранах).
   */
  readonly lang: ContentLang;
  /** Доп. CSS-класс корня (экран-скоуп раскладки: `--lobby` / `--landing`). */
  readonly variant?: string;
}

export interface BoardPreviewCardHandle {
  /** Корень карточки (`<div>`). Экран навешивает role/tabindex/click и кладёт в грид. */
  readonly element: HTMLElement;
  /** Имя режима (`.mode-card-name`) — экран НЕ трогает текст (он локализован примитивом). */
  readonly nameEl: HTMLElement;
  /** Строка имени (`.mode-card-name-row`) — host для «?»-попапа лобби (append справа от имени). */
  readonly nameRowEl: HTMLElement;
  /** Тело карточки (`.board-preview-card-body`) — host для ОПИСАНИЯ лендинга (append под именем). */
  readonly bodyEl: HTMLElement;
  /** Локализованное имя (готовое — экран использует в aria-label цели). */
  readonly name: string;
  /** Чистое размонтирование (board-view + appearance-подписка). */
  destroy(): void;
}

/**
 * Построить оболочку карточки-превью режима. Вертикаль (нейтральная структура):
 * [ живая мини-доска ] → [ строка имени ] → [ тело (слот) ]. Раскладку поверх этих
 * общих классов задаёт CSS-скоуп экрана; примитив — единый источник ХРОМА + ДОСКИ +
 * ЛОКАЛИЗОВАННОГО ИМЕНИ + paint-изоляции (см. шапку).
 */
export function createBoardPreviewCard(
  opts: BoardPreviewCardOptions,
): BoardPreviewCardHandle {
  const entry = opts.entry;
  const locked = !!entry.locked;
  const name = modeLabel(entry, opts.lang);

  const el = document.createElement('div');
  el.className = 'board-preview-card mode-card';
  if (opts.variant) el.classList.add(opts.variant);
  if (locked) el.classList.add('mode-card--locked');

  // ── Живая мини-доска (hero) — общий ствол board-view + общий рендер превью. ────
  const thumb = document.createElement('div');
  thumb.className = 'mode-card-thumb';
  thumb.setAttribute('aria-hidden', 'true');
  el.appendChild(thumb);

  const board: BoardViewHandle = mountBoardView(thumb, {
    interactive: false,
    ariaLabel: t('game.modeCard.previewAria', { name }),
  });
  // Превью рисуется в ОФОРМЛЕНИИ игрока (реактивно к смене в сессии) — общий ствол,
  // не дубль. Раньше жило только в лобби; вынос в примитив → лендинг получает то же.
  const appearance: LiveAppearanceHandle = attachLiveAppearance(board.frame);
  renderModeThumbPreview(board, entry.hintLevel);

  // Бейдж «Скоро» (locked) — оверлеем на углу доски (НЕ в строке имени), чтобы строка
  // имени была одинаковой у всех карточек (как в рабочем лобби).
  if (locked && entry.badge) {
    const badge = document.createElement('span');
    badge.className = 'mode-card-badge';
    badge.textContent = entry.badge;
    thumb.appendChild(badge);
  }

  // ── Строка имени (host для «?»-попапа лобби) ─────────────────────────────────
  const nameRow = document.createElement('div');
  nameRow.className = 'mode-card-name-row';

  const nameEl = document.createElement('span');
  nameEl.className = 'mode-card-name';
  nameEl.textContent = name;
  nameRow.appendChild(nameEl);

  // ── Тело (слот под описание лендинга) ────────────────────────────────────────
  const body = document.createElement('div');
  body.className = 'board-preview-card-body';
  body.appendChild(nameRow);
  el.appendChild(body);

  // ── СКРОЛЛ-ПЕРФ: НИКАКОГО JS-вмешательства в скролл (был ResizeObserver — УБРАН) ──
  // Прежний `content-visibility:auto` + `ResizeObserver`-пин `contain-intrinsic-size`
  // ОКАЗАЛИСЬ ПРИЧИНОЙ лага (Chrome cv-churn — дорогой re-render доски на кадре входа в
  // окно) и прыжка (Firefox scroll-anchoring срывался от reserve≠realH «дыхания» высоты),
  // см. эмпирический замер BEFORE/AFTER в шапке board-preview-card.css. Решение — чисто
  // CSS: кэш-композит-слой активной доски НА ПК (эталон серых locked-досок), реальная
  // высота карточек (стабильный scrollHeight). JS-наблюдателей за скроллом больше нет —
  // нечего держать на главном потоке во время скролла.

  return {
    element: el,
    nameEl,
    nameRowEl: nameRow,
    bodyEl: body,
    name,
    destroy(): void {
      appearance.detach();
      board.unmount();
      if (el.parentNode) el.parentNode.removeChild(el);
    },
  };
}
